const money = (value, currency = 'INR') => new Intl.NumberFormat('en-IN', {
    style: 'currency', currency, maximumFractionDigits: 2,
}).format(Number(value || 0));

const date = value => value
    ? new Intl.DateTimeFormat('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
    : '—';

const fallbackInvoiceNumber = subscription => `GTM-${new Date(subscription?.purchasedAt || Date.now()).getFullYear()}-${String(subscription?._id || 'RECEIPT').slice(-8).toUpperCase()}`;

const makePdfFromJpeg = (dataUrl, width, height) => {
    const image = atob(dataUrl.split(',')[1]);
    const pageWidth = 595.28;
    const pageHeight = 841.89;
    const content = `q\n${pageWidth} 0 0 ${pageHeight} 0 0 cm\n/Im1 Do\nQ`;
    const objects = [
        '<< /Type /Catalog /Pages 2 0 R >>',
        '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Resources << /XObject << /Im1 5 0 R >> >> /Contents 4 0 R >>`,
        `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
        `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${image.length} >>\nstream\n${image}\nendstream`,
    ];

    let pdf = '%PDF-1.4\n';
    const offsets = [0];
    objects.forEach((object, index) => {
        offsets.push(pdf.length);
        pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
    });
    const xref = pdf.length;
    pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`;
    offsets.slice(1).forEach(offset => { pdf += `${String(offset).padStart(10, '0')} 00000 n \n`; });
    pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`;

    const bytes = new Uint8Array(pdf.length);
    for (let i = 0; i < pdf.length; i += 1) bytes[i] = pdf.charCodeAt(i) & 0xff;
    return new Blob([bytes], { type: 'application/pdf' });
};

export const downloadSubscriptionInvoicePdf = async (subscription, user = {}) => {
    if (!subscription) return;
    if (document.fonts?.ready) await document.fonts.ready;

    const canvas = document.createElement('canvas');
    canvas.width = 1240;
    canvas.height = 1754;
    const ctx = canvas.getContext('2d');
    const burgundy = '#5a0d24';
    const wine = '#7a1733';
    const rose = '#b4737e';
    const ivory = '#fbf8f4';
    const ink = '#2e2630';
    const muted = '#746973';
    const border = '#ead8db';
    const customer = subscription.customerSnapshot || {};
    const invoiceNumber = subscription.invoiceNumber || fallbackInvoiceNumber(subscription);
    const total = subscription.totalPaidSnapshot ?? subscription.priceSnapshot ?? 0;
    const subtotal = subscription.subtotalSnapshot ?? total;
    const tax = subscription.taxSnapshot ?? 0;
    const currency = subscription.currency || 'INR';

    const rounded = (x, y, w, h, r, fill, stroke = null) => {
        ctx.beginPath();
        ctx.roundRect(x, y, w, h, r);
        if (fill) { ctx.fillStyle = fill; ctx.fill(); }
        if (stroke) { ctx.strokeStyle = stroke; ctx.lineWidth = 2; ctx.stroke(); }
    };
    const label = (text, x, y) => {
        ctx.fillStyle = wine;
        ctx.font = '700 18px "DM Sans", Arial';
        ctx.textAlign = 'left';
        ctx.fillText(text.toUpperCase(), x, y);
    };
    const write = (text, x, y, font = '400 20px "DM Sans", Arial', color = ink, align = 'left') => {
        ctx.fillStyle = color; ctx.font = font; ctx.textAlign = align; ctx.fillText(String(text ?? ''), x, y);
    };
    const wrap = (text, x, y, maxWidth, lineHeight, font = '400 20px "DM Sans", Arial', color = muted, maxLines = 4) => {
        ctx.font = font; ctx.fillStyle = color; ctx.textAlign = 'left';
        const words = String(text || '').split(/\s+/).filter(Boolean);
        let line = ''; let row = 0;
        for (const word of words) {
            const test = line ? `${line} ${word}` : word;
            if (ctx.measureText(test).width > maxWidth && line) {
                ctx.fillText(line, x, y + row * lineHeight); row += 1; line = word;
                if (row >= maxLines) break;
            } else line = test;
        }
        if (row < maxLines && line) ctx.fillText(line, x, y + row * lineHeight);
    };

    ctx.fillStyle = '#ffffff'; ctx.fillRect(0, 0, canvas.width, canvas.height);
    rounded(84, 72, 1072, 230, 34, '#f7f3f1', border);
    rounded(84, 72, 18, 230, 9, wine);
    rounded(126, 116, 118, 118, 28, '#ffffff');
    write('G', 185, 199, '700 72px "Cormorant Garamond", Georgia', wine, 'center');
    write('Gettimelam', 286, 155, '700 48px "Cormorant Garamond", Georgia', burgundy);
    write('MATRIMONY', 289, 188, '700 17px "DM Sans", Arial', rose);
    write('Thoughtful connections. Lasting relationships.', 289, 225, '400 18px "DM Sans", Arial', muted);
    rounded(876, 112, 224, 54, 27, wine);
    write('PAYMENT INVOICE', 988, 147, '700 18px "DM Sans", Arial', '#ffffff', 'center');
    write(invoiceNumber, 1100, 205, '700 20px "DM Sans", Arial', ink, 'right');
    write(date(subscription.purchasedAt), 1100, 236, '400 17px "DM Sans", Arial', muted, 'right');

    rounded(84, 336, 514, 252, 24, '#ffffff', border);
    rounded(618, 336, 538, 252, 24, '#ffffff', border);
    label('Sold by', 112, 380);
    write('HUNTSWORLD', 112, 424, '700 25px "DM Sans", Arial');
    write('GSTIN: 34EWSPM5689F1ZQ', 112, 468, '700 18px "DM Sans", Arial', ink);
    write('info@huntsworld.com', 112, 526, '500 18px "DM Sans", Arial', muted);

    label('Bill to', 650, 380);
    write(customer.name || user.name || user.businessName || 'Member', 650, 424, '700 25px "DM Sans", Arial');
    const address = [customer.addressLine1, customer.addressLine2, customer.city, customer.district, customer.state, customer.country].filter(Boolean).join(', ') || 'India';
    wrap(address, 650, 462, 450, 28);
    write(customer.email || user.email || '', 650, 526, '500 18px "DM Sans", Arial', muted);
    write(customer.mobile || user.mobile || '', 650, 555, '500 18px "DM Sans", Arial', muted);

    rounded(84, 620, 1072, 92, 20, ivory);
    label('Payment', 112, 655); label('Order ID', 428, 655); label('Payment ID', 776, 655);
    write(subscription.paymentStatus === 'paid' ? 'PAID' : String(subscription.paymentStatus || 'recorded').toUpperCase(), 112, 688, '700 20px "DM Sans", Arial', '#24804a');
    write(subscription.paymentOrderId || '—', 428, 688, '600 17px "DM Sans", Arial');
    write(subscription.paymentId || '—', 776, 688, '600 17px "DM Sans", Arial');

    rounded(84, 750, 1072, 76, 18, ink);
    write('ITEM DESCRIPTION', 112, 797, '700 18px "DM Sans", Arial', '#ffffff');
    write('VALIDITY', 716, 797, '700 18px "DM Sans", Arial', '#ffffff');
    write('QTY.', 938, 797, '700 18px "DM Sans", Arial', '#ffffff');
    write('TOTAL', 1122, 797, '700 18px "DM Sans", Arial', '#ffffff', 'right');
    rounded(84, 826, 1072, 130, 0, '#ffffff', border);
    write(`${subscription.planNameSnapshot || 'Membership'} plan`, 112, 878, '700 22px "DM Sans", Arial');
    write('Gettimelam membership subscription', 112, 914, '400 17px "DM Sans", Arial', muted);
    write(`${date(subscription.purchasedAt)} – ${date(subscription.expiresAt)}`, 716, 886, '500 17px "DM Sans", Arial');
    write('1', 956, 886, '600 19px "DM Sans", Arial', ink, 'center');
    write(money(total, currency), 1122, 886, '700 20px "DM Sans", Arial', ink, 'right');

    rounded(84, 1000, 560, 232, 24, ivory);
    write('Payment & support', 112, 1048, '700 23px "DM Sans", Arial');
    wrap('Payment received securely. This invoice was generated electronically and does not require a physical signature.', 112, 1090, 500, 29, '400 18px "DM Sans", Arial', muted, 4);
    write('Questions? info@huntsworld.com', 112, 1192, '500 17px "DM Sans", Arial', muted);

    rounded(674, 1000, 482, 232, 24, '#ffffff', border);
    write('Sub total', 704, 1048, '500 19px "DM Sans", Arial', muted);
    write(money(subtotal, currency), 1124, 1048, '500 19px "DM Sans", Arial', ink, 'right');
    write(`GST (${subscription.gstPercentageSnapshot ?? 0}%)`, 704, 1090, '500 19px "DM Sans", Arial', muted);
    write(money(tax, currency), 1124, 1090, '500 19px "DM Sans", Arial', ink, 'right');
    rounded(694, 1120, 442, 84, 17, wine);
    write('TOTAL PAID', 720, 1172, '700 21px "DM Sans", Arial', '#ffffff');
    write(money(total, currency), 1110, 1172, '700 24px "DM Sans", Arial', '#ffffff', 'right');

    // Print-safe branded footer at the bottom of the A4 page.
    const footerGradient = ctx.createLinearGradient(84, 0, 1156, 0);
    footerGradient.addColorStop(0, burgundy);
    footerGradient.addColorStop(0.55, wine);
    footerGradient.addColorStop(1, rose);
    ctx.fillStyle = footerGradient;
    ctx.fillRect(84, 1492, 1072, 7);
    write('Thank you for choosing Gettimelam Matrimony.', 620, 1543, '700 25px "Cormorant Garamond", Georgia', burgundy, 'center');
    write('HUNTSWORLD  |  info@huntsworld.com  |  GSTIN 34EWSPM5689F1ZQ', 620, 1582, '700 14px "DM Sans", Arial', muted, 'center');
    write('Gettimelam Matrimony is a Huntsworld service', 620, 1615, '400 14px "DM Sans", Arial', muted, 'center');
    write('This is a computer-generated payment invoice and does not require a physical signature.', 620, 1650, '400 13px "DM Sans", Arial', '#9b8e91', 'center');

    const blob = makePdfFromJpeg(canvas.toDataURL('image/jpeg', 0.96), canvas.width, canvas.height);
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${invoiceNumber}.pdf`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
};

export { fallbackInvoiceNumber, money as formatInvoiceMoney, date as formatInvoiceDate };
